import PropertySidebar from '@/components/PropertySidebar/PropertySidebar.vue';
import HeaderSection from '@/components/Header/HeaderSection.vue';
import BolideAssistant from '@/components/BolideAssistant/BolideAssistant.vue';
import { get_token, get_user_profile } from '@/router';
import { ajax_json_request, build_url_with_query, ensure_trailing_slash } from '@/components/Settings/settings_utils';
import is_admin_user from "@/utils/auth_user";

const active_property_storage_key = "bolide_active_property_context";
const assist_query_url = process.env.VUE_APP_ASSIST_QUERY_URL;
const assist_reports_url = assist_query_url
  ? ensure_trailing_slash(assist_query_url.replace(/\/query\/?$/, "/reports"))
  : "";

export default {
  name: "ResourcesView",

  components: {
    PropertySidebar,
    HeaderSection,
    BolideAssistant
  },

  data() {
    return ({
      tiles: [],
      selected_property: null,

      // Reports
      reports: [],
      reports_loading: false,
      reports_error: "",
      downloading_report_id: null,
      active_menu_item: "resources",
      showAssistant: false,
      sidebar_collapsed: true,
      is_mobile: window.innerWidth < 768,
      mobile_sidebar_open: false,

      // Scheduled Reports - static UI preview, not yet backed by automated
      // generation. See AssistReportRecipientConfig on the backend for the
      // real schedule/recipient model once this goes live.
      scheduled_report_examples: [
        {
          report_type: "Utility Performance",
          period_label: "April 2026",
          title: "Utility Performance Report - April 2026",
          status: "draft",
          generated_at: "10 Sept 2026"
        },
        {
          report_type: "CFO Meeting Pack",
          period_label: "September 2026",
          title: "CFO Meeting Pack - September 2026",
          status: "draft",
          generated_at: "10 Sept 2026"
        }
      ],

      breadcrumb: [
        { id: "main", title: "Portfolio Dashboard" },
        { id: "resources", title: "Resources" }
      ]
    });
  },

  computed: {
    current_user_profile() {
      return get_user_profile() || {};
    },

    can_view_portfolio() {
      return is_admin_user(this.current_user_profile);
    },

    assistant_scope() {
      return ({
        level: "property",
        property_id: this.selected_property?.id || null,
        property_name: this.selected_property?.title || this.selected_property?.name || null,
        domain_hint: "resources"
      });
    }
  },

  mounted() {
    const savedTiles = sessionStorage.getItem("tiles");
    const savedSelected = sessionStorage.getItem("selected_property");

    this.tiles = savedTiles ? JSON.parse(savedTiles) : [];
    this.selected_property = savedSelected ? JSON.parse(savedSelected) : null;
    this.update_breadcrumb();
    this.fetch_reports();
    window.addEventListener("resize", this.handle_resize);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.handle_resize);
  },

  methods: {

    handleTileClick(propertyId) {
      const found = this.tiles.find(t => t.id === propertyId);
      if (found) {
        this.selected_property = found;
        sessionStorage.setItem("selected_property", JSON.stringify(found));
        this.update_breadcrumb();
        this.fetch_reports();
      }
    },

    goBack() {
      this.$router.push({ name: "dashboard_view" });
    },

    handleBreadcrumbClick(id) {
      if (id === "main") this.goBack();
    },

    toggleSidebar() {
      this.sidebar_collapsed = !this.sidebar_collapsed;
    },

    handle_resize() {
      this.is_mobile = window.innerWidth < 768;
    },

    toggle_mobile_sidebar() {
      this.mobile_sidebar_open = !this.mobile_sidebar_open;
    },

    close_sidebar() {
      this.mobile_sidebar_open = false;
    },

    update_breadcrumb() {
      this.breadcrumb = [
        { id: "main", title: "Portfolio Dashboard" },
        { id: "resources", title: this.selected_property?.title || "Resources" }
      ];
    },

    handlePropertyMenuSelected(data) {
      const found = this.tiles.find(t => t.id === data.propertyId);
      if (found) {
        this.selected_property = found;
        sessionStorage.setItem("selected_property", JSON.stringify(found));
      }

      // Stay here when Resources is clicked
      if (data.menuItem === "resources") {
        this.active_menu_item = "resources";
        this.update_breadcrumb();
        this.fetch_reports();
        this.close_sidebar();
        return;
      }

      // Hand off to the main dashboard, preserving the property + menu item
      // it restores on mount (see dashboard_one.js open_initial_property_view).
      sessionStorage.setItem(active_property_storage_key, JSON.stringify({
        id: data.propertyId,
        title: data.propertyName || found?.title || "",
        menuItem: data.menuItem
      }));
      this.close_sidebar();
      this.$router.push({ name: "dashboard_view" });
    },

    async fetch_reports() {
      const property_name = this.selected_property?.title;
      if (!assist_reports_url || !property_name) {
        this.reports = [];
        return;
      }
      this.reports_loading = true;
      this.reports_error = "";
      try {
        const url = build_url_with_query(assist_reports_url, {
          property_name,
          limit: 20
        });
        const data = await ajax_json_request(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Token ${get_token()}`
          }
        });
        this.reports = Array.isArray(data?.results) ? data.results : [];
      } catch (error) {
        this.reports_error = error?.message || "Unable to load reports.";
        this.reports = [];
      } finally {
        this.reports_loading = false;
      }
    },

    async handle_download_report(report) {
      this.downloading_report_id = report.id;
      this.reports_error = "";
      try {
        const response = await fetch(`${assist_reports_url}${report.id}/pdf/download/`, {
          method: "GET",
          headers: { Authorization: `Token ${get_token()}` }
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.detail || data?.error || "Unable to download this report.");
        }
        const blob = await response.blob();
        const disposition = response.headers.get("Content-Disposition") || "";
        const filename_match = /filename="?([^";]+)"?/i.exec(disposition);
        const filename = filename_match?.[1] || `${report.property_name || "report"}-${report.id}.pdf`;

        const object_url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = object_url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(object_url);
      } catch (error) {
        this.reports_error = error?.message || "Unable to download this report.";
      } finally {
        this.downloading_report_id = null;
      }
    },

    format_report_type(report_type) {
      return String(report_type || "")
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ") || "Report";
    },

    format_report_date(value) {
      if (!value) return "";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "";
      return parsed.toLocaleDateString("en-ZA", { year: "numeric", month: "short", day: "numeric" });
    },

    report_status_badge_class(report_status) {
      if (report_status === "sent" || report_status === "approved") return "settings-soft-badge-success";
      if (report_status === "archived") return "settings-soft-badge-info";
      return "settings-soft-badge-pink";
    }
  }
};
